import React, {
	useContext,
	useMemo,
	useRef,
} from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { ConnectedUser } from '../../../../stores/types/connection';
import { contrastingColor, getNameOrInitials } from '../../../../utils/utils';
import { VideoStream } from '../../../types/videos';
import { GreenroomState as GS } from '../../../../stores/types/types';
import { cleanupVideoStreamElement } from '../../../../utils/video/video-streams';
import type { DragItem } from '../../../../stores/types/user-interface';
import { DRAG_TYPE } from '../../../../stores/reducers/user-interface';
import useDragItem from '../../../../hooks/use-dnd/use-drag-item';
import useUserActions from '../../../../hooks/use-user-actions';
import { AVContext } from '../../../../contexts/audio-video';
import type { ShotFeed } from '../../../../stores/types/shots';
import RestrictedUserTooltip from '../../../right-panel/restricted-user-tooltip';

interface Props {
	video: VideoStream;
	usersMap: any;
	feedsRef?: React.MutableRefObject<HTMLDivElement | null>;
}

export default function VideoFeedShotBuilder(props: Props) {
	const {
		video,
		usersMap,
	} = props;

	const host = useSelector((s: GS) => s.SessionReducer.host);
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const embed = useSelector((s: GS) => s.SessionReducer.embed);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const onsiteUsers = useSelector((s: GS) => s.SessionReducer.userMeetingSettings.onsiteUsers);
	const webrtcID = useSelector((s: GS) => s.SessionReducer.webrtcID);
	const { shot_data: { shotFeeds } } = useSelector((s: GS) => s.ShotsReducer.shotModalShot);

	const wrapperRef = useRef<HTMLDivElement | null>(null);

	const { getAV } = useContext(AVContext);

	const hasVideo = useMemo(() => {
		const key = video.id === webrtcID ? 'local' : video.id;
		return getAV(key).video;
	}, [getAV, webrtcID, video.id]);

	const { getFeedName } = useUserActions();

	const hasMultipleProducers = useMemo(
		() => connectedUsers.filter((user: ConnectedUser) => user.isProducer).length ?? 0 > 1,
		[connectedUsers],
	);

	const handlePlaying = (e: any) => {
		e.target.volume = 0;
	};

	const getPosterImage = (videoStream: VideoStream) => {
		const user = usersMap[videoStream.id];
		if (user && (user.poster || user.avatar)) {
			return user.poster || user.avatar;
		}
		return undefined;
	};

	const dndProps = useMemo<DragItem>(() => {
		if (video.isMedia && video.mediaId) {
			return { id: video.mediaId, type: DRAG_TYPE.FEED_MEDIA };
		}

		if (video.isScreenShare) {
			const user = connectedUsers.find(
				(connectedUser: ConnectedUser) => connectedUser.screenShareWebrtcId === video.id,
			);
			if (user) {
				return { id: user.email, type: DRAG_TYPE.FEED_SCREENSHARE };
			}
		} else if (video.isSipCall) {
			return { id: video.id, type: DRAG_TYPE.FEED_SIP_CALL };
		} else if (video.profile) {
			return { id: video.profile, type: DRAG_TYPE.FEED_PRESENTER };
		}

		return { id: 0, type: DRAG_TYPE.FEED_EMPTY };
	}, [video, connectedUsers]);

	const { dragRef, isDragging } = useDragItem(dndProps);

	const isOnsiteUser = useMemo(() => {
		if (video.profile && onsiteUsers?.includes(video.profile)) {
			return true;
		}
		return false;
	}, [onsiteUsers, video]);

	const inShot = useMemo(
		() => shotFeeds.some((feed: ShotFeed) => (feed.presenter === video.profile || feed.sipStreamId === video.id)),
		[video, shotFeeds],
	);

	return (
		<React.Fragment>
			{isDragging && (<div className="video-thumb-drag-placeholder" />)}
			<div
				id={String(video.id)}
				className={classNames('video-thumb', {
					'is-dragging': isDragging,
					'producer-thumbnail-large': !host && usersMap[video.id]?.isProducer && !hasMultipleProducers,
				})}
				ref={dragRef}
			>
				<div className="video-wrapper" ref={wrapperRef}>
					<video
						autoPlay
						muted
						playsInline
						onPlaying={handlePlaying}
						ref={(ref: any) => {
							if (ref && ref.srcObject !== video.srcObject) {
								/* eslint-disable-next-line no-param-reassign */
								ref.srcObject = video.srcObject as MediaStream;
								cleanupVideoStreamElement(ref);
							}
						}}
						style={{ pointerEvents: 'none', transform: video.self ? 'scaleX(-1)' : 'none' }}
					/>

					{!embed && (
						<>
							<div className={classNames('video-overlay')}>
								{!hasVideo && (
									<div className={classNames('poster')}>
										{/* eslint-disable-next-line no-extra-boolean-cast */}
										{!!getPosterImage(video) ? (
											<img className="poster-image" src={getPosterImage(video)} alt={''} />
										) : (
											<div className="no-image-placeholder">
												<span style={{
													backgroundColor: meeting?.primary_color ? `#${meeting.primary_color}` : '#333',
													color: contrastingColor(meeting?.primary_color ?? '#333333'),
												}}>
													{getNameOrInitials(usersMap[video.id], false)}
												</span>
											</div>
										)}
									</div>
								)}

								<RestrictedUserTooltip
									restrictedUserState={usersMap[video.id]?.restrictedUserState}
								/>

								<div className="video-overlay-bottom">
									<label className={classNames('font-size-14', 'font-weight-heavy', !host && usersMap[video.id]?.isProducer && !hasMultipleProducers ? 'is-producer' : '')}>
										{getFeedName(video)}
									</label>
								</div>

								<>
									{usersMap[video.id]?.isProducer && !isOnsiteUser && <div className="producer-label"><span>PRODUCER</span></div>}
									{usersMap[video.id]?.isProducer && isOnsiteUser && <div className="producer-label"
										data-testid="prod-onsite-label"><span>PROD-ONSITE</span></div>}
									{!usersMap[video.id]?.isProducer && isOnsiteUser && <div className="producer-label"
										data-testid="onsite-label"><span>ONSITE</span></div>}
								</>
							</div>
							<div className={classNames('selection-border', inShot && 'in-shot')} />
						</>
					)}
				</div>
			</div>
		</React.Fragment>
	);
}
